/**
 * Sincronización de catálogos entre vistas y pestañas del navegador.
 *
 * - Cuando un catálogo (laboratorios, presentaciones, marcas, etc.) cambia
 *   en una pestaña/ruta, se notifica a TODAS las pestañas abiertas vía
 *   BroadcastChannel. Las páginas que consumen ese catálogo recargan sus
 *   datos automáticamente, sin necesidad de F5.
 * - Fallback: storage event (para navegadores sin BroadcastChannel) + custom
 *   event in-tab para escuchas dentro de la misma pestaña.
 */

const CHANNEL_NAME = "pharma_catalog_sync";
const STORAGE_PING_KEY = "pharma_catalog_sync_ping";

let _bc = null;
function getChannel() {
  if (typeof window === "undefined") return null;
  if (_bc !== null) return _bc;
  try {
    _bc = new BroadcastChannel(CHANNEL_NAME);
  } catch (_) {
    _bc = false; // not supported
  }
  return _bc || null;
}

/** Emite un evento de catálogo actualizado.
 *  @param {string} name  - clave del catálogo (ej: "presentaciones")
 *  @param {string} action - "create" | "update" | "delete"
 */
export function emitCatalogUpdated(name, action = "update") {
  if (typeof window === "undefined" || !name) return;
  const payload = { name, action, ts: Date.now() };
  // 1) BroadcastChannel (entre pestañas)
  const bc = getChannel();
  if (bc) {
    try { bc.postMessage(payload); } catch (_) { /* ignore */ }
  }
  // 2) Fallback: storage event (entre pestañas en navegadores sin BC)
  try {
    localStorage.setItem(STORAGE_PING_KEY, JSON.stringify(payload));
  } catch (_) { /* ignore */ }
  // 3) Custom event (dentro de la misma pestaña)
  try {
    window.dispatchEvent(new CustomEvent("pharma:catalog-updated", { detail: payload }));
  } catch (_) { /* ignore */ }
}

/** Suscribe un handler a las actualizaciones de catálogos.
 *  @param {string[]|"*"} names  - lista de catálogos a escuchar, o "*" para todos
 *  @param {(payload:{name:string,action:string,ts:number}) => void} handler
 *  @returns {() => void} unsubscribe
 */
export function onCatalogUpdated(names, handler) {
  if (typeof window === "undefined") return () => {};
  const wanted = names === "*" ? null : new Set(Array.isArray(names) ? names : [names]);
  const match = (p) => !wanted || wanted.has(p?.name);

  const bcHandler = (ev) => { if (match(ev.data)) handler(ev.data); };
  const storageHandler = (ev) => {
    if (ev.key !== STORAGE_PING_KEY || !ev.newValue) return;
    try {
      const p = JSON.parse(ev.newValue);
      if (match(p)) handler(p);
    } catch (_) { /* ignore */ }
  };
  const customHandler = (ev) => { if (match(ev?.detail)) handler(ev.detail); };

  const bc = getChannel();
  if (bc) bc.addEventListener("message", bcHandler);
  window.addEventListener("storage", storageHandler);
  window.addEventListener("pharma:catalog-updated", customHandler);

  return () => {
    if (bc) bc.removeEventListener("message", bcHandler);
    window.removeEventListener("storage", storageHandler);
    window.removeEventListener("pharma:catalog-updated", customHandler);
  };
}
